"use client";

import { useState, useEffect } from "react";
import { X, Download } from "lucide-react";

const DISMISSED_KEY = "pwa_install_dismissed_until";
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if already dismissed recently
    const dismissedUntil = localStorage.getItem(DISMISSED_KEY);
    if (dismissedUntil && Date.now() < Number(dismissedUntil)) return;

    // Check if already installed (running in standalone mode)
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsVisible(true);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setIsVisible(false);
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem(
      DISMISSED_KEY,
      String(Date.now() + DISMISS_DURATION_MS),
    );
  };

  if (!isVisible) return null;

  return (
    <div
      role="banner"
      aria-label="Install Car Spares app"
      style={{
        position: "fixed",
        bottom: "calc(env(safe-area-inset-bottom, 0px) + 72px)", // above mobile bottom nav
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 9999,
        width: "calc(100% - 32px)",
        maxWidth: 420,
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)",
        padding: "14px 16px",
        display: "flex",
        alignItems: "center",
        gap: 12,
        animation: "slideUp 0.3s ease-out",
      }}
    >
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(16px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      {/* App icon */}
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: "#fffbeb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <img
          src="/icons/icon-192x192.png"
          alt="Car Spares"
          style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 6 }}
        />
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            margin: 0,
            fontSize: 13,
            fontWeight: 700,
            color: "#0f172a",
            lineHeight: 1.3,
          }}
        >
          Add Car Spares to your phone
        </p>
        <p
          style={{
            margin: "2px 0 0",
            fontSize: 11,
            color: "#64748b",
            lineHeight: 1.4,
          }}
        >
          Shop car parts faster with our app
        </p>
      </div>

      {/* Install button */}
      <button
        onClick={handleInstall}
        style={{
          background: "#f59e0b",
          color: "#1a1408",
          border: "none",
          borderRadius: 10,
          padding: "8px 14px",
          fontSize: 12,
          fontWeight: 700,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 5,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        <Download size={13} />
        Install
      </button>

      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        aria-label="Dismiss install banner"
        style={{
          background: "transparent",
          border: "none",
          cursor: "pointer",
          padding: 4,
          color: "#94a3b8",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <X size={16} />
      </button>
    </div>
  );
}
